/**
 * help.ts — the (?) help modal for the Reflex Bandit.
 *
 * Same pattern as the sibling demos (mob, fishing-demo): a persistent
 * question-mark button in the top-left corner mounts this modal on
 * document.body. Click outside, click ×, or press Esc to close. Singleton:
 * open is idempotent, close fully tears down.
 */

/** The blog post that accompanies this demo — the deep dive on tree-ensemble
 *  contextual bandits, drift detection, and the action-invariant encoding. */
export const ESSAY_URL = 'https://blog.vski.ai/posts/bandits-active-exploitation/';

export class HelpModal {
  readonly el: HTMLElement;
  private onKey: (e: KeyboardEvent) => void;

  constructor(onClose: () => void) {
    this.el = document.createElement('div');
    this.el.className = 'overlay help-overlay';
    this.el.innerHTML = `
      <div class="overlay-card help-card">
        <button class="help-close" aria-label="Close">×</button>
        <h2>Reflex Bandit</h2>
        <p class="help-lead">A tree-ensemble contextual bandit that learns what its tokens mean — from reward alone.</p>

        <p>An agent sits on a 15×15 grid. It can SEE the raw tokens of nearby cells (noiseless perception) — <strong>FREE</strong>, <strong>GOLD</strong>, <strong>STONE</strong> — but it does <em>not know what they mean</em>. Those are opaque labels. The agent must learn, from reward alone, that <code>GOLD</code> is the good token and <code>STONE</code> is a barrier. This is the "active exploitation" regime: not exploring an unknown map, but learning to <em>reflexively grab</em> the right token type as the world drifts.</p>

        <p>The model is an <strong>ExtraTrees ensemble</strong> (Extremely Randomized Trees, Geurts 2006) — 40 independent regression trees, each fit on the agent's accumulated transitions. For every candidate action the ensemble predicts the expected reward; the <em>mean</em> is the exploit signal, the <em>across-tree disagreement</em> is the exploration signal. <strong>Thompson Sampling</strong> picks one random tree's prediction per action and acts on the sample — naturally exploring when the trees disagree, exploiting when they agree.</p>

        <h3>Two-timescale memory + drift detector</h3>
        <p>Two buffers keep the model honest in a drifting world. The <strong>long</strong> buffer (never reset) carries token <em>semantics</em> — "GOLD = good" survives a day boundary. The <strong>day</strong> buffer (reset every <var>N</var> worlds) carries today's <em>shape</em> pattern — yesterday's arrangement is forgotten. At fit time the forest sees both. An <strong>EWMA drift detector</strong> (ECDD, Ross 2013) watches the absolute prediction error; when it spikes above the control limit, the agent opens a forced-random re-explore window so it collects fresh post-drift data before trusting its model again.</p>

        <h3>The action-invariant encoding (the load-bearing detail)</h3>
        <p>Each action's 75-dim one-hot token window (25 cells × 3 token values) lives in the <em>same</em> feature columns for every action, with an 8-dim one-hot action ID appended. So the destination token occupies the same feature index regardless of which action's window we're encoding — the tree learns ONE rule "GOLD at destination → reward" shared across all 8 action slots, not eight disjoint per-slot rules that each get ~1/8 of the data. An earlier action-specific encoding plateaued at 75% gold-grab; the invariant encoding clears 95%+ within a world.</p>

        <h3>How to read the canvas</h3>
        <ul class="help-legend">
          <li><span class="help-emoji coin"></span> <strong>Gold</strong> — the good token. The agent must learn this.</li>
          <li><span class="help-emoji rock"></span> <strong>Stone</strong> — a barrier. Bumping one already seen is punished (−0.5).</li>
          <li><span class="help-emoji window"></span> <strong>Percept window</strong> — the 5×5 cells the agent sees around itself. Noiseless; this is the bandit's context.</li>
          <li><span class="help-emoji agent"></span> <strong>Agent</strong> — the disc. Red flash = it bumped a discovered stone.</li>
        </ul>

        <h3>What the panel shows</h3>
        <table class="metric-table">
          <tr>
            <td class="metric-name">Arm</td>
            <td><strong>TS</strong> (Thompson Sampling), <strong>greedy</strong> (mean-only, no exploration), or <strong>random</strong> (baseline). The harness compares all three.</td>
          </tr>
          <tr>
            <td class="metric-name">Actions (bars)</td>
            <td>Per-action ensemble mean (the exploit signal). The chosen action turns green; the amber whisker is the across-tree std (the exploration signal). When all bars are equal the model has no preference and the agent explores via TS sampling.</td>
          </tr>
          <tr>
            <td class="metric-name">Drift status</td>
            <td><span style="color:var(--good)">OK</span> / <span style="color:var(--warn)">WARNING</span> / <span style="color:var(--bad)">DRIFT</span>. When DRIFT fires the agent acts uniform-random for 24 steps to collect fresh data, then trusts its model again.</td>
          </tr>
          <tr>
            <td class="metric-name">Day boundary</td>
            <td>Every <var>N</var> worlds the shape changes (circles → squares → circles → blobs). The day buffer resets; the long-term semantics survive. Watch gold-grab dip at the boundary, then recover.</td>
          </tr>
          <tr>
            <td class="metric-name">Run evaluation</td>
            <td>Spawns the 3-arm × 3-seed discrete + gradual harness in a worker. Reproduces the gold-per-day table, within-day speedup, drift-event timeline, and confusion matrices from the post.</td>
          </tr>
        </table>

        <h3>Try it</h3>
        <ul class="help-list">
          <li>Watch the first world: the agent moves randomly (ε-floor cold start, 64 obs). The moment it fits its first forest, the arm-bars snap to nonzero and it starts grabbing gold.</li>
          <li>Switch arm to <strong>greedy</strong> mid-run: it exploit-only and stops exploring. On a fresh shape it'll be slower to recover than TS.</li>
          <li>Drop <strong>min_samples_leaf</strong> to 3: trees overfit to cell-level patterns; within-day generalisation gets worse.</li>
          <li>Trigger a day boundary (let worlds-per-day cycle): watch the drift detector fire at the boundary, then gold-grab recover within a few worlds.</li>
          <li>Hit <strong>Run evaluation</strong> to reproduce the post's validation numbers in-browser.</li>
        </ul>

        <a class="essay-btn" href="${ESSAY_URL}" target="_blank" rel="noopener">Read the post →</a>
      </div>
    `;
    this.el.addEventListener('click', (e) => {
      if (e.target === this.el) onClose();
    });
    this.el.querySelector<HTMLButtonElement>('.help-close')!.addEventListener('click', onClose);
    this.onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', this.onKey);
  }

  mount(parent: HTMLElement): void {
    parent.appendChild(this.el);
  }

  remove(): void {
    document.removeEventListener('keydown', this.onKey);
    this.el.remove();
  }
}
